/* ⚛ REACT */
import { useEffect, useRef, useState } from "react";

/* 📦 LIBS */
import { toast } from "react-toastify";
import { toPng } from "html-to-image";
import { useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useTour } from "@reactour/tour";

/* 🧩 COMPONENTS */
import { Button, Input, InputCheckbox, Label, CustomLoading, Modal, SelectEquipment, Breadcrumb, Trash } from "../../../Components/index.js";
import { MultiGraph, LegendItem } from "../Components/index.js";

/* 🔗 SERVICE */
import { HistoricServices } from "../../../Services/historicosServices/index.js";

/* 📁 ASSETS*/
import { Setting, Ajuda } from "../../../Assets/icons/index.js";

/* 🧠 REDUX */
import { store } from "../../../Redux/Store/index.js";
import { useSelector } from "react-redux/es/hooks/useSelector";
import { changeOnlyGraphsData, changeOnlyGraphsLoading, changeModal } from "../../../Redux/Actions/index.js";

/* 🎨 STYLES */
import { 
    // HeaderContainer, GraphTitle, 
    HeaderContainerList, BodyContainer, Text, GraphContainer, LegendContainer, ContainerRow, ContainerColumn, 
    ContainerScale, NumberInput, ContainerVariaveis, ContainerVariaveisItem, ScaleText, ButtonContainer, FullHeader, 
    HeaderLeft, HeaderRight, DateContainer, EquipamentScale, WrapContainer, LegendWrap, ConfigContainer, Icon } from "./styles";
import { Container, HelpIcon } from "../../styles.js";

/* 🔧 UTILS */
import { isValidStartData, isValidEndData } from "../../../util/validate.js";
import { compareSteps } from "../../../util/tour/compare.js";

const OnlyGraphs = () => {
    const { t } = useTranslation();

    document.title = t("reports.sub-items.history.only-graphs.document-title");

    const navigationFlow =  [
        { text: 'reports', page: null },
        { text: 'graphs', page: null },
    ]

    const location = useLocation();
    let data1 = location.state?.data1;
    let data2 = location.state?.data2;

    const { isOpen, setIsOpen, currentStep, setSteps } = useTour();

    const [datainicio, setDatainicio] = useState(data1 ? data1 : new Date().toISOString(Date.now() - 3 * 60 * 60 * 1000).slice(0, 10) + 'T00:00');
    const [datafim, setDatafim] = useState(data2 ? data2 : new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString().slice(0, 10) + 'T23:59:59');

    const services = useRef(new HistoricServices());
    const [ids, setIds] = useState([]);
    const [config, setConfig] = useState([]);
    const [equipament, setEquipament] = useState('');
    const [isDataUpdated, setIsDataUpdated] = useState(false);
    const AllEquipaments = useRef([]);
    const graphRef = useRef(null);

    const [maxLeft, setMaxLeft] = useState();
    const [minLeft, setMinLeft] = useState();
    const [maxRight, setMaxRight] = useState();
    const [minRight, setMinRight] = useState();

    const isLoading = useSelector(state => state.HistoricReducer.isLoadingOnlyGraphs);
    const graphData = useSelector(state => state.HistoricReducer.onlyGraphsData);
    const equipaments = useSelector(state => state.HistoricReducer.equipamentList);
    const Cities = useSelector(state => state.HistoricReducer.cities);
    
    function formatarDataParaYMDHMS(data) {
        const year = data.getFullYear();
        const month = String(data.getMonth() + 1).padStart(2, '0');
        const day = String(data.getDate()).padStart(2, '0');
        const hours = String(data.getHours()).padStart(2, '0');
        const minutes = String(data.getMinutes()).padStart(2, '0');
        const seconds = String(data.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
    
    // eslint-disable-next-line
    const getData = async () => {
        if(ids.length === 0){
            toast(t("message.select-equipment"), {
                position: "top-center",
                type: 'info',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
            });
            return;
        }else{
            setIsDataUpdated(true);
            store.dispatch(changeOnlyGraphsLoading(true));
            let startData = formatarDataParaYMDHMS(new Date(datainicio));
            let endData = formatarDataParaYMDHMS(new Date(datafim));
            let arrayids = ids.length > 0 ? ids.map(item => item.id) : [];
            let tempdata = await services.current.getAllGraphData(arrayids, startData, endData);
            store.dispatch(changeOnlyGraphsLoading(false));
            setConfig(JSON.parse(JSON.stringify(tempdata)));
        }
    }

    /* eslint-disable */
    // Desativa warning de dependencia de variavel
    useEffect(() => {
        const getEquipaments = async () => {
            let temp = await services.current.getEquipamentos();
            AllEquipaments.current = temp;
        };

        getEquipaments();
        services.current.getCities();

        if(ids.length > 0){
            getData(); 
        }else{
            store.dispatch(changeOnlyGraphsData([]));
        }

        setSteps(compareSteps(t));

        return () => {
            setSteps([]);       
        };
    }, [])
    /* eslint-enable */

    const changeSide = (data, graphData) => {
        setConfig(JSON.parse(JSON.stringify(
            data.map(configItem => {
                if(Object.keys(configItem)[0] === Object.keys(graphData)[0]){
                    return graphData;
                }else{
                    return configItem;
                }
            })
        )))
        store.dispatch(changeOnlyGraphsData(JSON.parse(JSON.stringify(
            data.map(configItem => {
                if(Object.keys(configItem)[0] === Object.keys(graphData)[0]){
                    return graphData;
                }else{
                    return configItem;
                }
            })
        ))))
    }

    const showLegendBySide = (data, side) => {
        let groupedItems = Object.values(data)[0]
        .filter(item => ids.length > 0 && item.active && item.side === side)
        .reduce((groups, item) => {
            const key = item.id.split("=")[1];
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
            return groups;
        }, {});
        return(
            Object.entries(groupedItems).map(([groupKey, items]) => (
                <WrapContainer key={groupKey}>
                    <EquipamentScale>{groupKey}:</EquipamentScale>
                    {items.map((item, index) => (
                        <LegendItem key={index} text={item.id.split("=")[0]} color={item.color} />
                    ))}
                </WrapContainer>
            ))
        );
    }

    const downloadImage = () => {
        if (graphRef.current === null) return;
    
        toPng(graphRef.current)
            .then((dataUrl) => {
                const link = document.createElement('a');
                link.download = 'Gráfico.png';
                link.href = dataUrl;
                link.click();
            })
            .catch((err) => {
                console.error('Erro ao gerar imagem', err);
        });
    };

    const handleTour = async() => {
        if (ids.length === 0) {
            toast(t("message.compare.handle-tour"), {
                position: "top-center",
                type: 'info',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
            });
        } else {
            ids.map(item => (
                item.id = item.equipamentsFiltered[1].id
            ));
            await getData();
            setIsOpen(true);
        }
    }

    return (
        <>
            <Container>
                <FullHeader>
                    <div className="header">
                        <Breadcrumb flow={navigationFlow}/>
                        <Button color={"gradienteGreen"} onClick={() => handleTour()} icon={<HelpIcon src={Ajuda}/>} disabled={isLoading} padding={"8px 10px"} radius={"3px"} width={"44px"} />
                    </div>
                    <HeaderLeft>
                        {ids.map((item, index) => (
                            <HeaderContainerList id="variable-config" key={index}>	
                                <SelectEquipment Color={'white'} options={item.cities} value={item.city} onChange={(e) => {
                                    if(e.target.value === '' || e.target.value === null || e.target.value === undefined){
                                        item.equipamentsFiltered = AllEquipaments.current
                                        item.city = e.target.value
                                    }else{
                                        item.equipamentsFiltered = item.equipamentos.filter(equipament => parseInt(equipament.city_code) === parseInt(e.target.value) || equipament.city_code === undefined) 
                                        item.city = e.target.value
                                    }
                                    setIds(ids.map((item2, index2) => (index === index2 ? item : item2)))               
                                }} />
                                <SelectEquipment Color={'white'} options={item.equipamentsFiltered} value={item.id} onChange={async(e) => {
                                    if(e.target.value === '' || e.target.value === null || e.target.value === undefined || e.target.value === item.id){
                                        toast(t("message.select-equipment"), {
                                            position: "top-center",
                                            type: 'info',
                                            autoClose: 3000,
                                            hideProgressBar: false,
                                            closeOnClick: true,
                                            pauseOnHover: false,
                                            draggable: false,
                                            progress: undefined,
                                        });
                                        return
                                    }else if(ids.some(item => item.id === e.target.value)){
                                        toast('Equipamento já selecionado', {
                                            position: "top-center",
                                            type: 'info',
                                            autoClose: 3000,
                                            hideProgressBar: false,
                                            closeOnClick: true,
                                            pauseOnHover: false,
                                            draggable: false,
                                            progress: undefined,
                                        });
                                        return
                                    }else{
                                        setIsDataUpdated(false);
                                        item.id = e.target.value;
                                        let currentCity = await services.current.changeCity(e.target.value);
                                        item.city = currentCity;
                                        item.equipamentos = AllEquipaments.current;
                                        item.equipamentsFiltered = item.equipamentos.filter(equipament => parseInt(equipament.city_code) === parseInt(currentCity) || equipament.city_code === undefined);
                                        setIds(
                                            ids.map((item2, index2) => (
                                                index === index2 ? item : item2
                                            ))
                                        ) 
                                    }
                                }} />
                                <ButtonContainer>
                                    <Button 
                                    id={'add-variable-button'} 
                                    text={t("buttons.set-variable")} 
                                    icon={<Icon src={Setting} $disabled={isOpen && (currentStep === 1 || currentStep === 4)}/>} 
                                    disabled={isOpen && (currentStep === 1 || currentStep === 4)} 
                                    width={'fit-content'} 
                                    padding={"14px"}
                                    onClick={() => {
                                        if(!isDataUpdated){getData()}
                                        setEquipament({...item, name: item.equipamentsFiltered.filter(equipament => equipament.id === item.id)[0]?.nome})
                                        store.dispatch(changeModal('variavelconfig'))}}/>
                                    <Button icon={<Trash src={Trash}/>} disabled={isOpen && currentStep === 4} width={'fit-content'} color={'white-red'} onClick={() => {
                                        if(item.id === '' || item.id === null || item.id === undefined){
                                            setIds(ids.filter((item2, index2) => index !== index2))
                                        }else{
                                            setIds(ids.filter((item2, index2) => index !== index2))
                                            let allGraphs = Object.values(graphData[0])[0]
                                            allGraphs = allGraphs.filter(graph => ! graph.id.includes((item.equipamentsFiltered.filter(currentequipament => currentequipament.id === item.id)[0]?.nome)))
                                            store.dispatch(changeOnlyGraphsData(JSON.parse(JSON.stringify([{[Object.keys(graphData[0])[0]]: allGraphs}]))))
                                            setConfig(JSON.parse(JSON.stringify([{[Object.keys(graphData[0])[0]]: allGraphs}])))
                                        }
                                    }}/>
                                </ButtonContainer>
                            </HeaderContainerList>
                        ))}
                        {ids.length < 4 && equipaments.length > 1 &&
                            <Button 
                                id={'add-equipment-button'}
                                color={"green"}
                                width={'fit-content'}
                                text={t("buttons.add-equipment")}
                                disabled={isOpen && currentStep === 0}
                                onClick={() => setIds([...ids, {
                                    id: '',
                                    city: '',
                                    equipamentos: AllEquipaments.current,
                                    equipamentsFiltered: AllEquipaments.current,
                                    cities: Cities,
                                }])}
                            />
                        }
                    </HeaderLeft>

                    <HeaderRight id="calendar">
                        <DateContainer>
                            <Label text={t("inputs.initial-date.label")} aling="flex-start"/>
                            <Input 
                                aling="flex-start"
                                id='datainicio' 
                                name='datainicio' 
                                type="datetime-local" 
                                className={"custom-input"}
                                placeholder={t("inputs.initial-date.placeholder") }
                                value={datainicio}
                                max={datafim}
                                // innerRef={UserField}
                                invalidMessage={t("inputs.initial-date.invalid-message")}
                                validationFunction={(text) => isValidStartData(text, datafim)}
                                onchange={(text) => {
                                    setIsDataUpdated(false);
                                    text && setDatainicio(text)
                                }}/>
                        </DateContainer>
                        <DateContainer>
                            <Label text={t("inputs.final-date.label")} aling="flex-start"/>
                            <Input 
                                aling="flex-start"
                                id='datafim' 
                                name='datafim' 
                                type="datetime-local" 
                                className={"custom-input"}
                                placeholder={t("inputs.final-date.placeholder") }
                                value={datafim}
                                min={datainicio}
                                invalidMessage={t("inputs.final-date.invalid-message")}
                                validationFunction={(text) => isValidEndData(datainicio, text)}
                                onchange={(text) => {
                                    setIsDataUpdated(false);
                                    (text && !isNaN(new Date(text).getTime())) && setDatafim(text)
                                }}/> 
                        </DateContainer>
                        {(ids.length > 0 || (isOpen && currentStep === 2)) &&
                            <Button text={t("buttons.compare")} disabled={isOpen && currentStep === 2} width={'fit-content'} color={'green'} onClick={() => {getData()}}/>
                        }
                    </HeaderRight>
                </FullHeader>

                <BodyContainer>
                    {isLoading?
                        <CustomLoading width={'150px'} height={'150px'}/>
                    :
                    ids.length > 0 &&
                        graphData?.map((graph, index) => (
                            Object.values(graph)[0][0]?.data?.length > 0 &&
                            <div key={index}>
                                <ConfigContainer>
                                    <Button 
                                        text={t("buttons.download-Graph")} 
                                        width={'fit-content'} 
                                        color={'green'} 
                                        onClick={() => downloadImage()} 
                                    />
                                    <Button 
                                        id={'axis-config-button'} 
                                        text={t("buttons.axis-config")} 
                                        disabled={currentStep === 3} 
                                        width={'fit-content'} 
                                        color={'green'} 
                                        onClick={() => store.dispatch(changeModal('graphConfig'))} 
                                    />
                                </ConfigContainer>
                                <GraphContainer key={index} ref={graphRef}>     
                                    {/* <GraphTitle>
                                        {Object.keys(graph)[0]}
                                    </GraphTitle> */}

                                    <MultiGraph id={index} showLegends={true} 
                                        data={JSON.parse(JSON.stringify(Object.values(graph)[0]))} 
                                        LeftMax={maxLeft}
                                        LeftMin={minLeft}
                                        RightMax={maxRight}
                                        RightMin={minRight}
                                    />

                                    <LegendContainer>
                                        <LegendWrap>
                                            <ScaleText> {t("reports.sub-items.history.only-graphs.eixo-1")}:</ScaleText>
                                            {showLegendBySide(graph, 'left')}
                                        </LegendWrap>
                                        
                                        <LegendWrap>
                                            <ScaleText> {t("reports.sub-items.history.only-graphs.eixo-2")}:</ScaleText>
                                            {showLegendBySide(graph, 'right')}
                                        </LegendWrap>  
                                    </LegendContainer>
                                </GraphContainer>
                            </div>
                        ))        
                    }
                </BodyContainer>
            </Container>

            <Modal title={t("modal.environment-settings.title") + " " + equipament?.name} name={'variavelconfig'} width={'45%'} height={'70%'}>                                
                {!isLoading ? 
                    <>
                        <EquipamentScale> {t("reports.sub-items.history.only-graphs.eixo-1")}</EquipamentScale>
                        <ContainerVariaveis>
                            {equipament && config?.map((graph, index) => (
                                Object.values(graph)[0][0]?.data?.length > 0 &&
                                    Object.values(graph)[0].map((variavel, index) => (
                                        variavel.id.includes(equipament.equipamentsFiltered.filter(currentequipament => currentequipament.id === equipament.id)[0]?.nome) &&
                                        <ContainerVariaveisItem key={index}>
                                            <InputCheckbox key={index} id={variavel.id} name={variavel.id} text={variavel.id.split("=")[0]} 
                                                checked={variavel.active && variavel.side === 'left'} 
                                                onChange={async function(value){
                                                    variavel.active = value
                                                    if(value){
                                                        variavel.side = 'left'
                                                        changeSide(config, graph)
                                                        return true
                                                    }else{
                                                        changeSide(config, graph)
                                                        return true
                                                    }
                                            }}/>
                                        </ContainerVariaveisItem>
                                ))
                            ))}
                        </ContainerVariaveis>

                        <EquipamentScale> {t("reports.sub-items.history.only-graphs.eixo-2")}</EquipamentScale>
                        <ContainerVariaveis>
                            {equipament && config?.map((graph, index) => (
                                Object.values(graph)[0][0]?.data?.length > 0 &&
                                    Object.values(graph)[0].map((variavel, index) => (
                                        variavel.id.includes(equipament.equipamentsFiltered.filter(currentequipament => currentequipament.id === equipament.id)[0]?.nome) &&
                                        <ContainerVariaveisItem key={index}>
                                            <InputCheckbox key={index} id={variavel.id} name={variavel.id} text={variavel.id.split("=")[0]} 
                                                checked={variavel.active && variavel.side === 'right'} 
                                                onChange={async function(value){
                                                    variavel.active = value
                                                    if(value){
                                                        variavel.side = 'right'
                                                        changeSide(config, graph)
                                                        return true
                                                    }else{
                                                        changeSide(config, graph)
                                                        return true
                                                    }
                                            }}/>
                                        </ContainerVariaveisItem>
                                ))
                            ))}
                        </ContainerVariaveis>

                        <Button text={'OK'} width={'fit-content'} color={'green'} onClick={() => store.dispatch(changeModal(''))} />
                    </>
                    : <CustomLoading width={'150px'} height={'150px'}/>
                }
            </Modal>

            <Modal title={'Configurações dos Eixos'} name={'graphConfig'} width={'30%'} height={'40%'}>
                <ContainerRow>
                    <ContainerColumn>
                        <EquipamentScale>{t("reports.sub-items.history.only-graphs.eixo-1")}</EquipamentScale>
                        <ContainerScale>
                            <Text>Max: </Text>
                            <NumberInput type='number' value={maxLeft} onChange={(e) => setMaxLeft(e.target.value)}></NumberInput>
                        </ContainerScale>
                        <ContainerScale>
                            <Text>Min: </Text>
                            <NumberInput type='number' value={minLeft} onChange={(e) => setMinLeft(e.target.value)}></NumberInput>
                        </ContainerScale>

                        {/* {config?.map((graph, index)=>(
                            Object.values(graph)[0][0]?.data?.length > 0 &&
                                Object.values(graph)[0].map((variavel, index)=>(
                                    <InputCheckbox key={index} id={variavel.id} name={variavel.id} text={variavel.id} 
                                        checked={variavel.active && variavel.side === 'left'} 
                                        onChange={async function(value){
                                        if(Object.values(graph)[0].filter(configItem => configItem.active).length >= 4 && value && !variavel.active){
                                            toast('Número máximo de variáveis atingido', {
                                                position: "top-center",
                                                type: 'error',
                                                autoClose: 3000,
                                                hideProgressBar: false,
                                                closeOnClick: true,
                                                pauseOnHover: false,
                                                draggable: false,
                                                progress: undefined,
                                            });
                                            return false
                                        }else{
                                            variavel.active = value
                                            if(value){
                                                variavel.side = 'left'
                                                changeSide(config, graph)
                                                return true
                                            }else{
                                                changeSide(config, graph)
                                                return true
                                            }
                                        }
                                    }}/>
                            ))
                        ))} */}
                    </ContainerColumn>
                    
                    {/* <hr></hr> */}

                    <ContainerColumn>
                        <EquipamentScale>{t("reports.sub-items.history.only-graphs.eixo-2")}</EquipamentScale>
                        <ContainerScale>
                            <Text>Max: </Text>
                            <NumberInput type='number' value={maxRight} onChange={(e) => setMaxRight(e.target.value)}></NumberInput>
                        </ContainerScale>
                        <ContainerScale>
                            <Text>Min: </Text>
                            <NumberInput type='number' value={minRight} onChange={(e) => setMinRight(e.target.value)}></NumberInput>
                        </ContainerScale>
                        {/* {config?.map((graph, index)=>(
                            Object.values(graph)[0][0]?.data?.length > 0 &&
                                Object.values(graph)[0].map((variavel, index)=>(
                                    <InputCheckbox key={index} id={variavel.id} name={variavel.id} text={variavel.id} 
                                        checked={variavel.active && variavel.side === 'right'} 
                                        onChange={async function(value){
                                        if(Object.values(graph)[0].filter(configItem => configItem.active).length >= 4 && value && !variavel.active){
                                            toast('Número máximo de variáveis atingido', {
                                                position: "top-center",
                                                type: 'error',
                                                autoClose: 3000,
                                                hideProgressBar: false,
                                                closeOnClick: true,
                                                pauseOnHover: false,
                                                draggable: false,
                                                progress: undefined,
                                            });
                                            return false
                                        }else{
                                            variavel.active = value
                                            if(value){
                                                variavel.side = 'right'
                                                changeSide(config, graph)
                                                return true
                                            }else{
                                                changeSide(config, graph)
                                                return true
                                            }
                                        }
                                    }}/>
                            ))
                        ))} */}
                    </ContainerColumn>
                </ContainerRow>

                <Button text={'OK'} width={'fit-content'} color={'green'} onClick={() => store.dispatch(changeModal(''))} />                   
            </Modal>
        </>
    );
}

export default OnlyGraphs;
